import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import { mergeLegacyEntityElements } from "%/dynamic-component-editor/classes/legacy-entity-element-merge";
import { appEntityExtensionKey } from "%/dynamic-component-editor/classes/app-entity-extension-elements";

export default class BaseEntity {
  #contextName;
  #entityConfig;

  static LEGACY_ENTITIES = {};
  static INSPIRE_ENTITY;
  // Host elements an installed app entity gains beside its generic ones.
  static APP_ENTITY_EXTENSIONS = {};

  static make(contextName, entityConfig) {
    // A bespoke Legacy* entity class (Story/Show/Season/Episode/Event/…) is the
    // ONLY source of that entity's hand-written card elements (StoryTitle,
    // ShowCoverImage, …). The generic InspireEntity only ever mints the generic
    // DynamicApp*Element field elements from the config's `fields`.
    //
    // Under the context-model rework every legacy def is now field-bearing, so
    // the old "fields → InspireEntity, else → LEGACY_ENTITIES" order meant a
    // legacy context ALWAYS routed to the generic entity and its bespoke class
    // was never reached — its elements had no resolver, so a card built with
    // them rendered "Invalid Element" on the published site. Instead, when a
    // bespoke class exists, return BOTH: its bespoke elements UNION the generic
    // field elements (bespoke wins on a name clash). This is the single
    // chokepoint both the builder and the standalone production render pass
    // through, so it restores every legacy entity's elements on publish at once.
    // A legacy def carries appName:"CreatorStudio" and must still resolve to its
    // bespoke class, so only a REAL app entity skips the lookup. The lookup itself
    // is legacyKeyFor(), which tries the context name and then the entityType's
    // trailing segment.
    const skipsLegacy = Boolean(entityConfig?.appName) && !entityConfig?.isLegacyEntity;
    const legacyKey = skipsLegacy ? "" : this.legacyKeyFor(contextName, entityConfig);
    const LegacyClass = legacyKey ? this.LEGACY_ENTITIES[legacyKey] : null;
    const hasFields = Boolean(entityConfig?.fields && Object.keys(entityConfig.fields).length);

    if (LegacyClass && hasFields) {
      return this.#stampPaletteSource(
        this.#makeMergedLegacyEntity(contextName, entityConfig, LegacyClass),
        legacyKey,
        true,
      );
    }
    if (hasFields) {
      const extensions = this.APP_ENTITY_EXTENSIONS[appEntityExtensionKey(entityConfig)] ?? [];
      return new this.INSPIRE_ENTITY(contextName, entityConfig, extensions);
    }
    if (LegacyClass) {
      return this.#stampPaletteSource(new LegacyClass(contextName), legacyKey, false);
    }
    return new this.INSPIRE_ENTITY(contextName, entityConfig);
  }

  // What the elements palette needs to build its rows without re-deriving any
  // of make()'s resolution: which LEGACY_ENTITIES key holds this context's
  // hand-written rows, and whether getElements() also carries generic field
  // elements to union in. Stamped as own properties because a merged entity is
  // Object.create(bespoke) — it inherits the prototype but NOT the private
  // fields the entityConfig getter reads, so touching that getter on it throws.
  static #stampPaletteSource(entity, legacyKey, hasGenericFieldElements) {
    entity.legacyKey = legacyKey;
    entity.hasGenericFieldElements = hasGenericFieldElements;
    return entity;
  }

  // The LEGACY_ENTITIES key this context resolves to, or "" for an inspire
  // context. Mirrors the lookup make() performs: the context name first, then
  // the entityType's trailing segment.
  static legacyKeyFor(contextName, entityConfig) {
    if (this.LEGACY_ENTITIES[contextName]) {
      return contextName;
    }
    const entityType = entityConfig?.entityType;
    if (!entityType) {
      return "";
    }
    const key = entityType.split(".").at(-1);
    const lowerKey = key.charAt(0).toLowerCase() + key.slice(1);
    if (this.LEGACY_ENTITIES[lowerKey]) {
      return lowerKey;
    }
    return this.LEGACY_ENTITIES[key] ? key : "";
  }

  // A field-bearing legacy context: its elements are the bespoke class's card
  // elements plus the generic field elements. Both stay resolvable; only the
  // bespoke ones reach the palette — see legacy-entity-element-merge.js for why.
  static #makeMergedLegacyEntity(contextName, entityConfig, LegacyClass) {
    const bespoke = new LegacyClass(contextName);
    const generic = new this.INSPIRE_ENTITY(contextName, entityConfig);
    const merged = Object.create(bespoke);
    merged.getElements = () => mergeLegacyEntityElements(
      bespoke.getElements(),
      generic.getElements(),
    );
    return merged;
  }

  constructor(name, config) {
    this.#contextName = name;
    this.#entityConfig = config;
  }

  get entityConfig() {
    return this.#entityConfig;
  }

  get contextName() {
    return this.#contextName;
  }

  getElements() {
    throw new DynamicComponentError("\"getElements() is not implemented in your Entity class\"", {});
  }
}
